/*
 * The eight-character one-time code, on the client side (security.md §4.1). The server normalises
 * what it receives as well — this is not a validation the browser is trusted with — but a person who
 * types the O of "OK" where a zero was printed must see the code they meant while they type it, not
 * a refusal after they send it.
 *
 * Crockford base32 without I, L, O and U: I and L read as 1, O reads as 0, and everything outside
 * the alphabet (the hyphen of the printed XXXX-XXXX, a space, a pasted newline) is dropped.
 */

export const CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"

/** How many characters a whole code has. */
export const CODE_LENGTH = 8

const GROUP_SIZE = 4

/** The canonical form of what a person typed or pasted. */
export function normaliseCode(input: string): string {
	let out = ""
	for (const raw of input.toUpperCase()) {
		const c = raw === "I" || raw === "L" ? "1" : raw === "O" ? "0" : raw
		if (CODE_ALPHABET.includes(c)) out += c
	}
	return out
}

/**
 * How a code is shown while it is typed and how it is printed: XXXX-XXXX. The input normalises what
 * it is given first, so the reader sees the character the server will compare — the 0 they typed as
 * an O — rather than discovering the substitution when the code is refused.
 */
export function formatCode(code: string): string {
	const canonical = normaliseCode(code)
	return canonical.length > GROUP_SIZE
		? `${canonical.slice(0, GROUP_SIZE)}-${canonical.slice(GROUP_SIZE)}`
		: canonical
}

/** True once the input holds a whole code; the send button waits for it. */
export function isCompleteCode(input: string): boolean {
	return normaliseCode(input).length === CODE_LENGTH
}
